import { Check, Copy } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Callout } from '@/shared/components/feedback/Callout/Callout';
import { ErrorState } from '@/shared/components/feedback/ErrorState/ErrorState';
import { FormSkeleton } from '@/shared/components/feedback/Skeleton/Skeleton';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { Badge } from '@/shared/components/ui/Badge/Badge';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { RelativeTime } from '@/shared/components/ui/RelativeTime/RelativeTime';
import { cn } from '@/shared/utils/cn/cn.util';

import {
  deliveryStatusTone,
  failureInvestigation,
} from '../../utils/delivery-failure/delivery-failure.util';

import { useDeliveryDetailModalLogic } from './useDeliveryDetailModalLogic';

/**
 * What exactly we sent them.
 *
 * That is the one question a support role opens this log to ask, and the copy on
 * the delivery row is the only place it can be answered: a push and an in-app
 * row are two different sentences, and a push whose in-app half a preference
 * suppressed has no notification row behind it at all (P21a).
 *
 * It is also where a **refused retry** lands. The worker claimed the row first
 * and is right; showing the row as it now stands explains the refusal by the
 * state that caused it, where a toast would take the news away before it was
 * read (P19b).
 */
export function DeliveryDetailModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('notifications');
  const { delivery, isLoading, error, wasRetryRefused, reload, copyStatus, copyProviderMessageId } =
    useDeliveryDetailModalLogic(modalData);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t('deliveries.detail.title')}</DialogTitle>
          <DialogDescription>{t('deliveries.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-lg">
          {wasRetryRefused ? (
            <Callout tone="warning" liveness="alert" title={t('deliveries.retry.refused')}>
              {t('deliveries.retry.refusedHint')}
            </Callout>
          ) : null}

          {isLoading ? (
            <FormSkeleton fields={3} />
          ) : error !== null ? (
            <ErrorState error={error} title={t('deliveries.detail.loadFailed')} onRetry={reload} />
          ) : delivery === null ? null : (
            <>
              <div className="flex flex-wrap items-center gap-sm">
                <Badge tone={deliveryStatusTone(delivery.status)}>
                  {t(`deliveries.status.${delivery.status}`)}
                </Badge>
                <Badge tone="neutral">{t(`channel.${delivery.channel}`)}</Badge>
                {/* An LTR ISLAND — a dotted key inside Arabic prose reorders. */}
                <span dir="ltr" className="font-mono text-caption text-muted-foreground">
                  {delivery.templateKey}
                </span>
              </div>

              <section className="flex flex-col gap-xs">
                <h4 className="text-label font-medium text-foreground">
                  {t('deliveries.detail.copy')}
                </h4>
                {delivery.title === null && delivery.body === null ? (
                  <p className="text-caption text-muted-foreground">
                    {t('deliveries.detail.noCopy')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-xs rounded-md border border-border bg-muted/40 p-md">
                    <p className="text-label font-semibold text-foreground">{delivery.title}</p>
                    <p className="whitespace-pre-wrap text-caption text-muted-foreground">
                      {delivery.body}
                    </p>
                  </div>
                )}
              </section>

              {delivery.failureReason === null ? null : (
                <section className="flex flex-col gap-xs">
                  <h4 className="text-label font-medium text-foreground">
                    {t(`deliveries.reason.${delivery.failureReason}`)}
                  </h4>
                  <p className="text-caption text-muted-foreground">
                    {t(`deliveries.investigation.${failureInvestigation(delivery.failureReason)}`)}
                  </p>
                </section>
              )}

              <section className="flex flex-col gap-xs">
                <h4 className="text-label font-medium text-foreground">
                  {t('deliveries.detail.lastError')}
                </h4>
                <p
                  // An LTR ISLAND: a vendor's error string is English and often
                  // carries identifiers, so it reorders inside an Arabic panel.
                  dir="ltr"
                  className="whitespace-pre-wrap break-words text-caption text-muted-foreground"
                >
                  {delivery.lastError ?? t('deliveries.detail.noError')}
                </p>
              </section>

              <dl className="grid grid-cols-1 gap-md sm:grid-cols-3">
                <Fact label={t('deliveries.detail.attempts')}>
                  <span className="tabular-nums text-body">{delivery.attempts}</span>
                </Fact>
                <Fact label={t('deliveries.detail.createdAt')}>
                  <RelativeTime value={delivery.createdAt} className="text-body" />
                </Fact>
                <Fact label={t('deliveries.detail.sentAt')}>
                  {delivery.sentAt === null ? (
                    <span className="text-body text-muted-foreground">
                      {t('deliveries.detail.never')}
                    </span>
                  ) : (
                    <RelativeTime value={delivery.sentAt} className="text-body" />
                  )}
                </Fact>
                {delivery.deadLetteredAt === null ? null : (
                  <Fact label={t('deliveries.detail.deadAt')}>
                    <RelativeTime value={delivery.deadLetteredAt} className="text-body" />
                  </Fact>
                )}
                {/* Only where the channel has one: push and in-app carry no
                    vendor id, and a blank fact would read as a missing one. */}
                {delivery.providerMessageId === null ? null : (
                  <Fact label={t('deliveries.detail.providerMessageId')} className="sm:col-span-3">
                    <span className="flex min-w-0 items-center gap-xs">
                      {/* An LTR ISLAND — an SMTP Message-ID or a vendor SID
                          inside Arabic prose reorders its brackets. */}
                      <span dir="ltr" className="min-w-0 break-all font-mono text-caption">
                        {delivery.providerMessageId}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t(
                          copyStatus === 'copied'
                            ? 'deliveries.detail.copied'
                            : 'deliveries.detail.copyProviderMessageId',
                        )}
                        onClick={copyProviderMessageId}
                      >
                        {copyStatus === 'copied' ? (
                          <Check aria-hidden className="h-4 w-4 text-success" />
                        ) : (
                          <Copy aria-hidden className="h-4 w-4" />
                        )}
                      </Button>
                    </span>
                  </Fact>
                )}
              </dl>
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('common:action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Fact({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-2xs', className)}>
      <dt className="text-caption text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
